import { useState, useCallback, useEffect, useRef } from 'react';
import type { Id } from '../../convex/_generated/dataModel';
import type { IPropertySceneExtended } from '@/@types/propertySceneEditor';

export function usePropertySceneEditorHandlers(
  videoId: Id<'propertyVideos'>,
  propertyId: Id<'propertyListings'>,
  selectedDuration: number,
  selectedAspectRatio: '16:9' | '9:16',
  scenes: IPropertySceneExtended[],
  isPlanningScenes: boolean,
  hasTriggeredPlanning: boolean,
  planScenes: (
    propertyId: Id<'propertyListings'>,
    targetDuration: number,
    aspectRatio: '16:9' | '9:16'
  ) => Promise<void>,
  imageSelectionScene: IPropertySceneExtended | null,
  promptModalScene: IPropertySceneExtended | null,
  updateSelectedImage: (sceneId: Id<'propertyScenes'>, imageId: Id<'_storage'>) => Promise<void>,
  updateImagePrompt: (sceneId: Id<'propertyScenes'>, prompt: string) => Promise<void>,
  updateSceneNegativePrompt: (
    sceneId: Id<'propertyScenes'>,
    negativePrompt: string
  ) => Promise<void>,
  regenerateVideo: (sceneId: Id<'propertyScenes'>) => Promise<void>,
  closePromptModal: () => void
) {
  const [isRegeneratingPrompt, setIsRegeneratingPrompt] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSceneIndex, setLightboxSceneIndex] = useState(0);
  const planningTriggeredRef = useRef(false);

  useEffect(() => {
    if (
      scenes &&
      scenes.length === 0 &&
      !isPlanningScenes &&
      !hasTriggeredPlanning &&
      !planningTriggeredRef.current &&
      videoId &&
      propertyId
    ) {
      planningTriggeredRef.current = true;
      planScenes(propertyId, selectedDuration, selectedAspectRatio);
    }
  }, [
    scenes,
    isPlanningScenes,
    hasTriggeredPlanning,
    videoId,
    propertyId,
    selectedDuration,
    selectedAspectRatio,
    planScenes,
  ]);

  useEffect(() => {
    planningTriggeredRef.current = false;
  }, [videoId]);

  const handleImageSelect = useCallback(
    async (imageId: Id<'_storage'>) => {
      if (imageSelectionScene) {
        await updateSelectedImage(imageSelectionScene._id, imageId);
      }
    },
    [imageSelectionScene, updateSelectedImage]
  );

  const handlePromptSave = useCallback(
    async (positivePrompt: string, negativePrompt: string) => {
      if (promptModalScene) {
        await updateImagePrompt(promptModalScene._id, positivePrompt);
        await updateSceneNegativePrompt(promptModalScene._id, negativePrompt);
        closePromptModal();
      }
    },
    [promptModalScene, updateImagePrompt, updateSceneNegativePrompt, closePromptModal]
  );

  const handlePromptSaveAndGenerate = useCallback(
    async (positivePrompt: string, negativePrompt: string) => {
      if (promptModalScene) {
        await Promise.all([
          updateImagePrompt(promptModalScene._id, positivePrompt),
          updateSceneNegativePrompt(promptModalScene._id, negativePrompt),
        ]);
        await regenerateVideo(promptModalScene._id);
        closePromptModal();
      }
    },
    [
      promptModalScene,
      updateImagePrompt,
      updateSceneNegativePrompt,
      regenerateVideo,
      closePromptModal,
    ]
  );

  const handleRegeneratePrompt = useCallback(async () => {
    setIsRegeneratingPrompt(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsRegeneratingPrompt(false);
  }, []);

  const handleExpandVideo = useCallback(
    (sceneId: Id<'propertyScenes'>) => {
      const sceneIndex = scenes.findIndex(s => s._id === sceneId);
      if (sceneIndex >= 0) {
        setLightboxSceneIndex(sceneIndex);
        setLightboxOpen(true);
      }
    },
    [scenes]
  );

  const handleCloseLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const handlePlanScenes = useCallback(async () => {
    if (propertyId) {
      await planScenes(propertyId, selectedDuration, selectedAspectRatio);
    }
  }, [propertyId, selectedDuration, selectedAspectRatio, planScenes]);

  return {
    isRegeneratingPrompt,
    lightboxOpen,
    lightboxSceneIndex,
    handleImageSelect,
    handlePromptSave,
    handlePromptSaveAndGenerate,
    handleRegeneratePrompt,
    handleExpandVideo,
    handleCloseLightbox,
    handlePlanScenes,
  };
}
